/**
 * API client for sending incoming calls somewhere else.
 *
 * Its own file rather than more lines in `lib/telephony.ts`, for the reason
 * `lib/callSuppression.ts` gives: that file is the SETTINGS surface, edited once
 * in a while by somebody sitting down to configure the phone. This is a switch
 * thrown from the call log on the way out of the office, and it has its own two
 * endpoints precisely so that leaving is not a configuration session.
 *
 * The stored shape (`CallDivertSettings`) is shared with the settings page,
 * which reads and writes it as part of `inbound` - see `lib/telephony.ts`.
 */
import { api } from "./api";
import type { BusinessDay } from "./telephony";

/**
 * A standing forwarding rule: these weekdays, these hours, between these dates.
 *
 * The same week shape the working-hours schedule uses, and the same editor
 * draws it - with ONE difference in meaning that the card has to say out loud: a
 * ticked day whose window is empty (from equal to to) is the WHOLE day here,
 * where in the working week it would read as closed.
 */
export interface CallDivertSchedule {
  enabled: boolean;
  timezone: string;
  /** `Date.getDay()` order, 0 is Sunday. `open` means "forward on this day". */
  days: BusinessDay[];
  /** Inclusive calendar bounds, "YYYY-MM-DD", "" for unbounded. */
  from: string;
  to: string;
}

/** The forwarding group as it is stored. Both screens edit this same object. */
export interface CallDivertSettings {
  /** The manual switch: send every incoming call to `to` right now. */
  enabled: boolean;
  /** Where a forwarded call goes, E.164, "" for none. */
  to: string;
  /** ISO instant the manual switch lapses on its own, or "" for no expiry. */
  until: string;
  /** Forward automatically when the call arrives outside the working hours. */
  afterHours: boolean;
  /** Seconds the forwarded number rings. Held below a carrier's own voicemail. */
  ringSeconds: number;
  /** Show the caller's own number on the forwarded phone rather than our line. */
  showCaller: boolean;
  /** The standing rule: days, hours and an optional date range. */
  schedule: CallDivertSchedule;
}

/** Why a call is being sent away from the desk. */
export type CallDivertReason = "manual" | "schedule" | "after-hours";

/**
 * What is set, and what a call arriving right now would really do.
 *
 * `active` is the field the screen draws, not `enabled`. The two come apart
 * whenever an expiry has passed, the number has been cleared, the phone is not
 * answered at all, or the automatic rule is firing on a Sunday - and the
 * question an operator is asking the rail is "where do my calls go", not "which
 * switches did somebody press".
 */
export interface CallDivertState extends CallDivertSettings {
  active: boolean;
  reason: CallDivertReason | null;
  /** Incoming calls are answered at all, and Twilio is configured. */
  inboundReady: boolean;
  /** The working-hours schedule is on, so the automatic rule has a moment to
   *  fire on. With it off, "outside working hours" describes nothing. */
  hoursEnabled: boolean;
  /** Is the office open at this instant, by that schedule? */
  openNow: boolean;
  /** The saved number is our own line, which would loop back to us. */
  self: boolean;
}

export function getCallDivert(): Promise<{ divert: CallDivertState }> {
  return api.get("/admin/crm/calls/divert");
}

/**
 * Throw the manual switch.
 *
 * `to` is optional and only sent when the number is being changed; the server
 * keeps the stored one otherwise. `until` is the moment forwarding lapses on its
 * own, as an ISO instant - empty is "until somebody switches it off", and it is
 * ignored when switching off.
 *
 * Refuses rather than silently doing nothing: a number that cannot be dialled
 * answers 422 `invalid_number`, switching on with nothing to forward to answers
 * `no_target`, our own line answers `self_target`, and a moment that is not
 * ahead of now answers `invalid_until`.
 */
export function setCallDivert(input: {
  enabled: boolean;
  to?: string;
  until?: string;
}): Promise<{ divert: CallDivertState }> {
  return api.put("/admin/crm/calls/divert", input);
}
